import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import {
  Check,
  Copy,
  Globe,
  KeyRound,
  MoreHorizontal,
  Plus,
  Search,
  Server,
  Smartphone,
  Tag,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader, PageToolbar } from "@/components/page-header";
import { StatusDot } from "@/components/status-dot";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogInset,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import type { ProviderListItem } from "@/lib/types";
import { cn, relativeTime } from "@/lib/utils";

export const Route = createFileRoute("/_app/admin/providers")({
  beforeLoad: ({ context }) => {
    if (context.user.role !== "admin") throw redirect({ to: "/devices" });
  },
  component: AdminProvidersPage,
});

/** The list card every state of this page is drawn in, as Vercel's Deployments list. */
const LIST_CARD =
  "overflow-hidden rounded-lg bg-background-100 shadow-border divide-y divide-gray-alpha-400";

function AdminProvidersPage() {
  const qc = useQueryClient();
  const { data: providers } = useQuery({
    ...trpc.provider.list.queryOptions(),
    refetchInterval: 10_000,
  });

  const [creating, setCreating] = useState(false);
  const [tokensFor, setTokensFor] = useState<string | null>(null);
  /** Client-side only: a farm has a handful of providers, all already loaded. */
  const [query, setQuery] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: trpc.provider.list.queryKey() });

  const remove = useMutation(
    trpc.provider.remove.mutationOptions({
      onSuccess: () => {
        toast.success("Provider deleted");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const onDelete = (p: ProviderListItem) => {
    if (confirm(`Delete provider "${p.name}" and all its devices?`)) {
      remove.mutate({ id: p.id });
    }
  };

  const needle = query.trim().toLowerCase();
  const shown = providers?.filter(
    (p) =>
      !needle ||
      [p.name, p.id, p.publicBaseUrl, p.version ?? ""].some((s) =>
        s.toLowerCase().includes(needle),
      ),
  );

  return (
    <div className="flex flex-col">
      <PageHeader
        title="Providers"
        meta={providers && <span className="tabular-nums">{providers.length} total</span>}
      />

      {/* Vercel's list toolbar: search flexes, the primary action closes the
          row. The page's one primary action lives here, not in the top bar. */}
      <PageToolbar className="mb-3 flex-nowrap">
        <Input
          type="search"
          aria-label="Search providers"
          placeholder="Search providers…"
          className="h-10 flex-1 [&>input]:h-full"
          prefix={<Search className="size-4" />}
          prefixStyling={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button className="h-10" prefix={<Plus />} onClick={() => setCreating(true)}>
          Add provider
        </Button>
      </PageToolbar>

      {!providers ? (
        <ul className={LIST_CARD} aria-hidden>
          {[0, 1].map((i) => (
            <li key={i} className="flex h-[72px] items-center gap-4 px-4">
              <div className="grid flex-1 gap-2 lg:max-w-52">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3.5 w-16" />
              </div>
              <Skeleton className="hidden h-4 w-24 lg:block" />
              <Skeleton className="hidden h-4 w-48 lg:block" />
            </li>
          ))}
        </ul>
      ) : providers.length === 0 ? (
        <EmptyState
          icon={<Server />}
          title="No providers yet"
          description="A provider is a machine the phones are plugged into. Register one here, issue it a token, then start it with that token — it dials out to the coordinator, so it needs no inbound access."
        >
          <Button
            variant="secondary"
            className="h-10"
            prefix={<Plus />}
            onClick={() => setCreating(true)}
          >
            Add provider
          </Button>
        </EmptyState>
      ) : shown?.length === 0 ? (
        <div className={cn(LIST_CARD, "px-6 py-10 text-center")}>
          <p className="font-semibold text-gray-1000 text-label-14">No providers match</p>
          <p className="mt-1 text-copy-14 text-gray-900">
            Nothing has “{query.trim()}” in its name, id, URL or version.
          </p>
        </div>
      ) : (
        <>
          {/* Columns only from `lg`: with the sidebar open, `md` leaves too
              little width for the URL column to say anything before it
              truncates, and the stacked rows below carry every field. */}
          <ul className={cn(LIST_CARD, "hidden lg:block")}>
            {shown?.map((p) => (
              <li
                key={p.id}
                className="flex h-[72px] items-center gap-4 pr-3 pl-4 transition-colors hover:bg-gray-alpha-100"
              >
                <div className="w-52 min-w-0 shrink-0 xl:w-60">
                  <div className="truncate font-semibold text-gray-1000 text-label-14">
                    {p.name}
                  </div>
                  <div className="truncate text-gray-900 text-label-13-mono">{p.id}</div>
                </div>

                <div className="w-40 shrink-0">
                  <ConnectionStatus provider={p} />
                  <div
                    className="truncate text-gray-900 text-label-14 tabular-nums"
                    title={p.lastSeenAt ? new Date(p.lastSeenAt).toLocaleString() : undefined}
                  >
                    {lastSeen(p)}
                  </div>
                </div>

                <div className="flex w-32 shrink-0 items-center gap-2 text-gray-1000 text-label-14 tabular-nums">
                  <Smartphone className="size-4 text-gray-900" />
                  {p.deviceCount} {p.deviceCount === 1 ? "device" : "devices"}
                </div>

                <div className="grid min-w-0 flex-1 gap-0.5">
                  <span
                    className="flex min-w-0 items-center gap-2 text-gray-1000"
                    title={p.publicBaseUrl}
                  >
                    <Globe className="size-4 shrink-0 text-gray-900" />
                    <span className="truncate text-label-13-mono">{p.publicBaseUrl}</span>
                  </span>
                  <span className="flex min-w-0 items-center gap-2 text-gray-900">
                    <Tag className="size-4 shrink-0" />
                    {p.version ? (
                      <span className="truncate text-label-13-mono">
                        {formatVersion(p.version)}
                      </span>
                    ) : (
                      // Not a value, so not in the values' mono face.
                      <span className="truncate text-gray-700 text-label-14">
                        No version reported
                      </span>
                    )}
                  </span>
                </div>

                <span
                  className="hidden shrink-0 text-right text-gray-900 text-label-14 tabular-nums xl:block"
                  title={new Date(p.createdAt).toLocaleString()}
                >
                  Added {formatDay(p.createdAt)}
                </span>

                <ProviderMenu
                  provider={p}
                  onTokens={() => setTokensFor(p.id)}
                  onDelete={() => onDelete(p)}
                />
              </li>
            ))}
          </ul>

          <ul className={cn(LIST_CARD, "lg:hidden")}>
            {shown?.map((p) => (
              <li key={p.id} className="flex items-start gap-3 py-3.5 pr-2 pl-4">
                <div className="grid min-w-0 flex-1 gap-1">
                  <div className="flex min-w-0 items-baseline gap-2">
                    <span className="max-w-[70%] shrink-0 truncate font-semibold text-gray-1000 text-label-14">
                      {p.name}
                    </span>
                    <span className="min-w-0 truncate text-gray-900 text-label-13-mono">
                      {p.id}
                    </span>
                  </div>
                  <div className="flex min-w-0 items-center gap-1.5 text-label-14">
                    <ConnectionStatus provider={p} />
                    <span className="truncate text-gray-900 tabular-nums">· {lastSeen(p)}</span>
                  </div>
                  <p className="truncate text-gray-900 text-label-14 tabular-nums">
                    {p.deviceCount} {p.deviceCount === 1 ? "device" : "devices"}
                    {p.version && (
                      <>
                        {" · "}
                        <span className="text-label-13-mono">{formatVersion(p.version)}</span>
                      </>
                    )}
                  </p>
                  <p className="truncate text-gray-900 text-label-13-mono">{p.publicBaseUrl}</p>
                </div>
                {/* Pulled up so its centre sits on the name's line rather than
                    below it: under a finger the button is 40px to a 20px line. */}
                <ProviderMenu
                  className="-mt-1.5 pointer-coarse:-mt-2.5"
                  provider={p}
                  onTokens={() => setTokensFor(p.id)}
                  onDelete={() => onDelete(p)}
                />
              </li>
            ))}
          </ul>
        </>
      )}

      <CreateProviderDialog open={creating} onOpenChange={setCreating} onCreated={refresh} />
      <TokensDialog providerId={tokensFor} onOpenChange={() => setTokensFor(null)} />
    </div>
  );
}

function ConnectionStatus({ provider }: { provider: ProviderListItem }) {
  return (
    <span className="inline-flex shrink-0 items-center gap-2 text-gray-1000 text-label-14">
      <StatusDot tone={provider.connected ? "ready" : "canceled"} />
      {provider.connected ? "Connected" : "Offline"}
    </span>
  );
}

function lastSeen(p: ProviderListItem) {
  return p.lastSeenAt ? `seen ${relativeTime(p.lastSeenAt)}` : "never seen";
}

/** `0.6.2` reads as a version once it has its `v`; `fake-0.1.0` already does. */
function formatVersion(version: string) {
  return /^\d/.test(version) ? `v${version}` : version;
}

/** Vercel's list date: "Apr 22", with the year only once it isn't this one. */
function formatDay(date: Date | string) {
  const d = new Date(date);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
  });
}

function ProviderMenu({
  provider,
  onTokens,
  onDelete,
  className,
}: {
  provider: ProviderListItem;
  onTokens: () => void;
  onDelete: () => void;
  className?: string;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="tertiary"
          size="icon-sm"
          className={cn("shrink-0 pointer-coarse:size-10", className)}
          aria-label={`Actions for ${provider.name}`}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuItem onSelect={onTokens}>
          Manage tokens
          <KeyRound className="ml-auto" />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem variant="error" onSelect={onDelete}>
          Delete provider
          <Trash2 className="ml-auto" />
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function CreateProviderDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: () => void;
}) {
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [publicBaseUrl, setPublicBaseUrl] = useState("https://");

  const create = useMutation(
    trpc.provider.create.mutationOptions({
      onSuccess: () => {
        toast.success("Provider created — issue it a token next");
        onCreated();
        onOpenChange(false);
        setId("");
        setName("");
        setPublicBaseUrl("https://");
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Add provider</DialogTitle>
          <DialogDescription className="text-pretty">
            The provider dials out to the coordinator, so it needs no inbound access. The public
            base URL is what the <em>browser</em> uses to reach it directly for video and uploads —
            the coordinator never connects to it.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-6"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate({ id, name, publicBaseUrl });
          }}
        >
          <DialogInset className="flex flex-col gap-5">
            <div className="grid gap-2">
              <Label htmlFor="p-id">Identifier</Label>
              <Input
                id="p-id"
                required
                placeholder="lab-1"
                pattern="[a-z0-9][a-z0-9-]*"
                className="h-10 font-mono"
                value={id}
                onChange={(e) => setId(e.target.value)}
              />
              <p className="text-copy-13 text-gray-900">
                Lowercase letters, digits and dashes. Cannot be changed later.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="p-name">Name</Label>
              <Input
                id="p-name"
                required
                placeholder="Lab 1"
                className="h-10"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="p-url">Public base URL</Label>
              <Input
                id="p-url"
                required
                type="url"
                placeholder="https://lab-1.example.com"
                className="h-10 font-mono"
                value={publicBaseUrl}
                onChange={(e) => setPublicBaseUrl(e.target.value)}
              />
              <p className="text-copy-13 text-gray-900">
                Must be HTTPS in production — WebCodecs requires a secure context.
              </p>
            </div>
          </DialogInset>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={create.isPending}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TokensDialog({
  providerId,
  onOpenChange,
}: {
  providerId: string | null;
  onOpenChange: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  /** Held in component state only: the server cannot show it again. */
  const [issued, setIssued] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data: tokens } = useQuery({
    ...trpc.provider.tokens.list.queryOptions({ providerId: providerId ?? "" }),
    enabled: !!providerId,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: trpc.provider.tokens.list.queryKey() });

  const create = useMutation(
    trpc.provider.tokens.create.mutationOptions({
      onSuccess: (result) => {
        setIssued(result.token);
        setLabel("");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const revoke = useMutation(
    trpc.provider.tokens.revoke.mutationOptions({
      onSuccess: () => {
        toast.success("Token revoked");
        refresh();
      },
      onError: (e) => toast.error(e.message),
    }),
  );

  const dismissIssued = () => {
    setIssued(null);
    setCopied(false);
  };

  return (
    <Dialog
      open={!!providerId}
      onOpenChange={(v) => {
        if (!v) dismissIssued();
        onOpenChange(v);
      }}
    >
      {/* Vercel's "Token Created" modal is narrower than its others: it holds
          one field and one button, centred. */}
      <DialogContent showCloseButton={false} className={cn(issued && "sm:max-w-[448px]")}>
        {issued ? (
          // A view of its own rather than a banner over the list: the one thing
          // to do here is copy the token, and the token list underneath made
          // "Revoke" the nearest button to it.
          <>
            <DialogHeader className="items-center text-center">
              <DialogTitle className="sm:text-heading-20">Token issued</DialogTitle>
              <DialogDescription className="text-copy-14 text-gray-1000">
                Start <span className="font-mono text-[13px]">{providerId}</span> with this token;
                if it is lost, issue a new one and revoke this one.{" "}
                <strong className="block font-semibold">
                  Only a hash is stored, so it can't be shown again.
                </strong>
              </DialogDescription>
            </DialogHeader>

            <div className="grid">
              <Label htmlFor="t-issued" className="sr-only">
                Token
              </Label>
              <Input
                id="t-issued"
                readOnly
                value={issued}
                className="h-10 [&>input]:h-full [&>input]:font-mono [&>input]:text-[13px] pointer-coarse:[&>input]:text-[13px]"
                onFocus={(e) => e.currentTarget.select()}
                suffixStyling={false}
                suffix={
                  <Button
                    variant="tertiary"
                    size="icon-sm"
                    className="-mr-1 text-gray-900 hover:text-gray-1000"
                    aria-label={copied ? "Copied" : "Copy token"}
                    onClick={async () => {
                      await navigator.clipboard.writeText(issued);
                      setCopied(true);
                      toast.success("Copied");
                    }}
                  >
                    {copied ? <Check /> : <Copy />}
                  </Button>
                }
              />
            </div>

            <DialogFooter>
              <Button variant="secondary" onClick={dismissIssued}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Provider tokens</DialogTitle>
              <DialogDescription>
                Machine credentials for{" "}
                <span className="font-mono text-[13px] text-gray-1000">{providerId}</span>. Only a
                hash is stored, so a token is shown exactly once.
              </DialogDescription>
            </DialogHeader>

            <DialogInset className="flex flex-col gap-4">
              <form
                className="flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (providerId) create.mutate({ providerId, label: label || undefined });
                }}
              >
                <div className="grid min-w-0 flex-1 gap-2">
                  <Label htmlFor="t-label">Label (optional)</Label>
                  <Input
                    id="t-label"
                    className="h-10"
                    placeholder="lab-1 host"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                  />
                </div>
                <Button type="submit" className="h-10" loading={create.isPending}>
                  Issue token
                </Button>
              </form>

              {tokens?.length ? (
                <ul className="divide-y divide-gray-alpha-400 rounded-lg bg-background-100 shadow-border">
                  {tokens.map((t) => (
                    <li key={t.id} className="flex items-center gap-3 py-3 pr-3 pl-4">
                      <div className="grid min-w-0 flex-1 gap-0.5">
                        <div className="flex min-w-0 items-baseline gap-2">
                          <span
                            className={cn(
                              "max-w-[60%] shrink-0 truncate font-medium text-label-14",
                              t.label ? "text-gray-1000" : "text-gray-700",
                            )}
                          >
                            {t.label ?? "unlabelled"}
                          </span>
                          <span className="truncate text-gray-700 text-label-12-mono" title={t.id}>
                            {t.id}
                          </span>
                        </div>
                        <p className="text-pretty text-gray-900 text-label-13 tabular-nums">
                          Created {relativeTime(t.createdAt)}
                          {t.createdByName && ` by ${t.createdByName}`} · Last used{" "}
                          {relativeTime(t.lastUsedAt)}
                        </p>
                      </div>
                      {t.revokedAt ? (
                        <span
                          className="inline-flex shrink-0 items-center gap-2 text-gray-900 text-label-14"
                          title={`Revoked ${relativeTime(t.revokedAt)}`}
                        >
                          <StatusDot tone="canceled" />
                          Revoked
                        </span>
                      ) : (
                        <Button
                          size="small"
                          variant="secondary"
                          className="shrink-0 text-red-900"
                          onClick={() => revoke.mutate({ tokenId: t.id })}
                        >
                          Revoke
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="rounded-lg bg-background-100 px-4 py-6 text-center shadow-border">
                  <p className="font-semibold text-gray-1000 text-label-14">No tokens yet</p>
                  <p className="mt-1 text-copy-14 text-gray-900">
                    Issue one above, then start the provider with it.
                  </p>
                </div>
              )}
            </DialogInset>

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Done</Button>
              </DialogClose>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
